import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/EmptyState";
import { ShareButton } from "@/components/ShareButton";
import {
  Trophy, Calendar, MapPin, Users, Building2, Shield, ExternalLink,
  ChevronLeft, Clock, FileText, Globe, ListChecks, Tag, Info, Sparkles,
} from "lucide-react";
import { showError } from "@/lib/errors";

const LEVEL_LABEL = {
  local: "Local",
  territorial: "Territorial",
  national: "National",
  regional: "Régional",
  international: "International",
  professionnel: "Professionnel",
};

const STATUS_LABEL = {
  prevue: { label: "Prévue", tone: "secondary" },
  en_cours: { label: "En cours", tone: "default" },
  terminee: { label: "Terminée", tone: "outline" },
  annulee_reportee: { label: "Annulée / reportée", tone: "destructive" },
};

const SOURCE_LABEL = {
  admin: "Administration AITO.PF",
  copf: "COPF",
  federation: "Fédération",
  club: "Club",
  athlete: "Athlète",
  contributor: "Contributeur",
  calendar: "Calendrier interne",
};

function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="h-4 w-4 mt-0.5 text-[hsl(var(--aito-lagoon))] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-sm">{children || <span className="italic text-muted-foreground">Non renseigné</span>}</div>
      </div>
    </div>
  );
}

function LinkedEntities({ title, icon: Icon, ids, loader, linkBase, dataTestId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ids || ids.length === 0) { setItems([]); return; }
      try {
        const results = await Promise.all(ids.map((id) => loader(id).catch(() => null)));
        if (!cancelled) setItems(results.filter(Boolean));
      } catch { if (!cancelled) setItems([]); }
    })();
    return () => { cancelled = true; };
  }, [JSON.stringify(ids)]); // eslint-disable-line

  if (!ids || ids.length === 0) return null;
  return (
    <Card className="p-4" data-testid={dataTestId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
        <h3 className="font-display font-semibold">{title}</h3>
        <Badge variant="outline" className="text-[10px]">{ids.length}</Badge>
      </div>
      {items === null ? <Skeleton className="h-12 w-full" /> : items.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">Aucune entité retrouvée.</p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((it) => (
            <li key={it.id}>
              <Link to={`${linkBase}/${it.slug || it.id}`} className="text-sm text-[hsl(var(--aito-lagoon))] hover:underline inline-flex items-center gap-1.5">
                {it.full_name || it.name}
                <ExternalLink className="h-3 w-3" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export default function CompetitionDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [comp, setComp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/competitions/${slug}`)
      .then((r) => setComp(r.data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else showError(e, { prefix: "Compétition :" });
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 py-8 space-y-4" data-testid="competition-detail-loading">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (notFound || !comp) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 py-12">
        <EmptyState
          icon={Trophy}
          title="Compétition introuvable"
          description="Ce contenu n'existe pas ou a été retiré du référentiel AITO.PF."
        />
        <div className="text-center mt-6">
          <Button variant="outline" onClick={() => navigate("/competitions")}>← Retour aux compétitions</Button>
        </div>
      </div>
    );
  }

  const status = STATUS_LABEL[comp.competition_status] || { label: comp.competition_status, tone: "outline" };

  return (
    <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 py-8 space-y-6" data-testid="competition-detail-page">
      <Link to="/competitions" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="back-to-competitions">
        <ChevronLeft className="h-3.5 w-3.5" /> Toutes les compétitions
      </Link>

      <div className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Trophy className="h-5 w-5 text-[hsl(var(--aito-gold))]" />
          {comp.discipline && <Badge variant="outline" className="capitalize" data-testid="comp-discipline">{comp.discipline}</Badge>}
          {comp.level && <Badge variant="secondary" data-testid="comp-level">{LEVEL_LABEL[comp.level] || comp.level}</Badge>}
          <Badge variant={status.tone} data-testid="comp-status">{status.label}</Badge>
          {comp.validation_status && comp.validation_status !== "validated" && (
            <Badge variant="outline" className="text-[10px] uppercase">
              {comp.validation_status === "pending" ? "À valider" : comp.validation_status}
            </Badge>
          )}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight" data-testid="comp-name">{comp.name}</h1>
        {comp.description && <p className="text-base text-muted-foreground" data-testid="comp-description">{comp.description}</p>}
        <div className="pt-2">
          <ShareButton
            kind="competition"
            slug={comp.slug || comp.id}
            title={`${comp.name} — AITO.PF`}
            text={[comp.discipline, comp.location, comp.level].filter(Boolean).join(" · ")}
            canonicalPath={`/competitions/${comp.slug || comp.id}`}
            dataTestId="competition-share-button"
          />
        </div>
      </div>

      {comp.image_url && (
        <Card className="overflow-hidden">
          <img src={comp.image_url} alt={comp.name} className="w-full max-h-80 object-cover" />
        </Card>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <Card className="p-4 divide-y divide-border" data-testid="comp-info">
          <Row icon={Calendar} label="Date de début">{fmtDate(comp.date_start)}</Row>
          <Row icon={Clock} label="Date de fin">{fmtDate(comp.date_end)}</Row>
          <Row icon={MapPin} label="Lieu">{comp.location}</Row>
          <Row icon={Globe} label="Île / commune">
            {[comp.island, comp.commune].filter(Boolean).join(" — ") || null}
          </Row>
          <Row icon={Building2} label="Organisateur">{comp.organizer}</Row>
        </Card>

        <Card className="p-4 divide-y divide-border" data-testid="comp-meta">
          <Row icon={Tag} label="Niveau">{LEVEL_LABEL[comp.level] || comp.level}</Row>
          <Row icon={Info} label="Statut">{status.label}</Row>
          <Row icon={Sparkles} label="Source de déclaration">
            {SOURCE_LABEL[comp.source_type] || comp.source_type}
          </Row>
          <Row icon={ExternalLink} label="Lien officiel">
            {comp.official_url ? (
              <a href={comp.official_url} target="_blank" rel="noreferrer" className="text-[hsl(var(--aito-lagoon))] hover:underline break-all" data-testid="comp-official-url">{comp.official_url}</a>
            ) : null}
          </Row>
          <Row icon={ListChecks} label="Résultats / brackets">
            {comp.results_url ? (
              <a href={comp.results_url} target="_blank" rel="noreferrer" className="text-[hsl(var(--aito-lagoon))] hover:underline break-all" data-testid="comp-results-url">{comp.results_url}</a>
            ) : null}
          </Row>
        </Card>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <LinkedEntities
          title="Athlètes liés"
          icon={Users}
          ids={comp.athletes_linked || []}
          loader={(id) => api.get(`/athletes/${id}`).then((r) => r.data)}
          linkBase="/athletes"
          dataTestId="comp-athletes"
        />
        <LinkedEntities
          title="Clubs liés"
          icon={Shield}
          ids={comp.clubs_linked || []}
          loader={(id) => api.get(`/clubs/${id}`).then((r) => r.data)}
          linkBase="/clubs"
          dataTestId="comp-clubs"
        />
        <LinkedEntities
          title="Fédérations liées"
          icon={Building2}
          ids={comp.federations_linked || []}
          loader={(id) => api.get(`/federations/${id}`).then((r) => r.data)}
          linkBase="/federations"
          dataTestId="comp-federations"
        />
      </div>

      {comp.description && (
        <Card className="p-4" data-testid="comp-description-card">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
            <h3 className="font-display font-semibold">Description</h3>
          </div>
          <p className="text-sm whitespace-pre-line">{comp.description}</p>
        </Card>
      )}
    </div>
  );
}
